import { Dayjs } from "dayjs";
import { TFunction } from "i18next";
import { z } from "zod";

import { validateContactRow } from "@/components/alerts/add-v2/model/contactRows";
import {
  entityDraftHasContent,
  validateEntityDraft,
} from "@/components/alerts/add-v2/model/entityRules";
import { validateIncidentTime } from "@/components/alerts/add-v2/model/incidentTimes";
import { validateLocationRow } from "@/components/alerts/add-v2/model/locationRows";
import {
  BulletinContact,
  BulletinEntity,
  BulletinIncidentTime,
  BulletinLanguage,
  BulletinLocation,
  BulletinMediaItem,
  BulletinPdf,
} from "@/components/alerts/add-v2/model/types";
import { ALERT_CLASSIFICATION } from "@/components/alerts/constants";
import { IAssociatedCrime, IPoliceService } from "@/types";

/**
 * The whole-document schema, built per bulletin type.
 *
 * ## What it does and does not decide
 *
 * It decides **what is wrong**, in one place, for the whole page. It does not decide **when to say
 * so**: the always-visible count reads every issue this produces (`validation/sectionErrors.ts`),
 * while a field only goes red once react-hook-form has validated it — on the user's own change, or
 * on the `trigger()` that a blocked Finalize fires. That split is the design's "live errors on what
 * you typed, blank-field errors after a Finalize attempt", and it needs no state of its own.
 *
 * ## Why the rules are not written here
 *
 * Every conditional rule is a call into `model/`. Those functions predate this file — README
 * decision 6 — and phase 3's job was to agree with them, not to restate them. `superRefine` is what
 * lets a Zod schema call a plain function and still report against a field path.
 *
 * ## Fields with no rule
 *
 * They are still listed, as `opaque<T>()`. A Zod schema is the only description of this form that a
 * type checker reads, so a field missing from it is a field nobody notices is unvalidated; and
 * `Dayjs`, `IPoliceService` and the Google Maps types cannot be parsed by Zod in any useful way, so
 * `opaque` is what "carried, not checked" looks like. `phase3.test.tsx` pins the inferred type
 * against `BulletinFormValues` so the two cannot drift.
 *
 * ## Messages
 *
 * Translated here rather than emitted as codes, because a Zod issue carries one string and every
 * consumer — the count, the jump, the field — wants the same sentence. `t` is therefore an
 * argument, and a language switch rebuilds the schema (`useBulletinResolver`).
 */

/** Carried through the schema untouched. See "Fields with no rule" above. */
const opaque = <T>() => z.custom<T>(() => true);

export const bulletinSchema = (bulletinType: string | null, t: TFunction) =>
  z
    .object({
      /* Setup */
      policeService: opaque<IPoliceService | null>().refine(
        (value) => value !== null,
        { message: t("validation.police_service_required") },
      ),
      bulletinType: z
        .string()
        .nullable()
        .refine((value) => !!value, {
          message: t("validation.bulletin_type_required"),
        }),
      occurrenceNumbers: z
        .array(z.string())
        .min(1, t("validation.occurrence_required")),
      // Required only when the selected service defines areas, so the rule needs both fields and
      // lives in the object-level refinement below.
      operationalArea: z.string().nullable(),

      /* Incident */
      associatedCrimes: z
        .array(opaque<IAssociatedCrime>())
        .min(1, t("validation.crimes_required")),
      incidentTimes: z
        .array(opaque<BulletinIncidentTime>())
        .superRefine((rows, ctx) => {
          rows.forEach((row, index) => {
            const errors = validateIncidentTime(row);
            (["startDate", "endDate"] as const).forEach((field) => {
              const code = errors[field];
              if (!code) return;
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, field],
                message: t(`validation.incident_time_${code}`),
              });
            });
          });
        }),
      locations: z
        .array(opaque<BulletinLocation>())
        .superRefine((rows, ctx) => {
          // A bulletin needs an address. Until it has one, an empty row is *that* problem rather than
          // a second one, so the row-level "fill in or remove" only speaks once another row is filled
          // — otherwise a blank form would report the same missing address twice.
          const anyAddress = rows.some((row) => row.input !== null);
          if (!anyAddress) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [0, "input"],
              message: t("validation.location_required"),
            });
          }

          rows.forEach((row, index) => {
            const errors = validateLocationRow(row);
            (
              ["input", "classification", "otherClassification"] as const
            ).forEach((field) => {
              const code = errors[field];
              if (!code) return;
              if (field === "input" && !anyAddress) return;
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, field],
                message: t(`validation.location_${code}`),
              });
            });
          });
        }),

      /* People, vehicles & items */
      entities: z.array(opaque<BulletinEntity>()).superRefine((rows, ctx) => {
        rows.forEach((entity, index) => {
          // Exactly the rows `withSavableEntities` keeps. Clicking Add puts a blank row in the
          // document immediately (decision 9); counting its required fields would mean the button
          // itself adds two or three to "N required fields left" before anything has been typed.
          if (!entityDraftHasContent(entity) && !entity.autoAdded) return;

          Object.entries(validateEntityDraft(entity, bulletinType)).forEach(
            ([field, code]) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, field],
                message: t(`validation.entity_${code}`),
              });
            },
          );
        });
      }),

      /* Media — optional, and the only section with no rule at all. */
      media: z.array(opaque<BulletinMediaItem>()),
      pdf: opaque<BulletinPdf | null>(),

      /* Narrative — which of the two fields is required depends on the language, so see below. */
      description: z.string(),
      descriptionFr: z.string(),
      originalLanguage: opaque<BulletinLanguage>(),

      /* Contacts & approval */
      contacts: z.array(opaque<BulletinContact>()).superRefine((rows, ctx) => {
        rows.forEach((contact, index) => {
          Object.entries(validateContactRow(contact, index, rows)).forEach(
            ([field, code]) => {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, field],
                message: t(`validation.contact_${code}`),
              });
            },
          );
        });
      }),
      approvedBy: z.string(),
      approvedByBadge: z.string(),
      classification: opaque<ALERT_CLASSIFICATION | null>().refine(
        (value) => value !== null,
        { message: t("validation.classification_required") },
      ),
      issuedTime: opaque<Dayjs | null>(),
    })
    .superRefine((values, ctx) => {
      // The old flow's `validateOperationalArea`: required exactly when the service defines areas,
      // which is also the condition that decides whether the field is rendered at all.
      const hasAreas =
        (values.policeService?.rubi_org?.operational_areas ?? []).length > 0;
      if (hasAreas && !values.operationalArea) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["operationalArea"],
          message: t("validation.operational_area_required"),
        });
      }

      // Only the language the bulletin was written in is required — the other field is a
      // translation nobody has asked for yet.
      const narrativeField =
        values.originalLanguage === "fr" ? "descriptionFr" : "description";
      if (!values[narrativeField].trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [narrativeField],
          message: t("validation.narrative_required"),
        });
      }
    });

export type BulletinSchema = ReturnType<typeof bulletinSchema>;

/**
 * `bulletinSchema` memoised on the pair it is built from.
 *
 * A module-level cache rather than a `useMemo` because the resolver is not a hook: it is handed to
 * `useForm` once and called on every keystroke, and the bulletin type it needs is a *form value*,
 * so it can only be read from the values the resolver is given. See `useBulletinResolver`.
 *
 * The cache is keyed by `t` as well, so a language switch rebuilds rather than serving stale
 * English. It holds one entry: the type changes rarely, and the language less often than that.
 */
let cached: {
  bulletinType: string | null;
  t: TFunction;
  schema: BulletinSchema;
} | null = null;

export const cachedBulletinSchema = (
  bulletinType: string | null,
  t: TFunction,
): BulletinSchema => {
  if (cached && cached.bulletinType === bulletinType && cached.t === t) {
    return cached.schema;
  }

  const schema = bulletinSchema(bulletinType, t);
  cached = { bulletinType, t, schema };
  return schema;
};
